/**
 * Read-only intake bridge for the surviving vanilla-JS engine.
 *
 * Phase 2 of the pilot React-engine port (see
 * `docs/eng/pilot-react-engine.md`). React owns intake state via
 * `PilotContext` + `<IntakeSelect>`. The engine's surviving consumers
 * (story.js, main.js) read the current intake through this module so
 * they don't have to re-introduce the imperative listener pattern that
 * lived in the deleted `intake.js`.
 *
 * Architectural invariant: this module is **read-only**. Writes happen
 * exclusively through `PilotContext.setIntakeField`. The bridge:
 *
 *   - reads `mp-intake` from localStorage on every call (no in-memory
 *     cache — the React provider is the source of truth), and
 *   - exposes an `onIntakeChange` subscribe that hooks into the
 *     `pilot:intake-changed` CustomEvent dispatched by the provider.
 *
 * Engine consumers MUST NOT call `localStorage.setItem('mp-intake', …)`
 * from this surface. If you need to mutate intake, route the call
 * through the React surface.
 */

import type {Intake} from './processes';

export type IntakeField = keyof Intake;

const STORAGE_KEY = 'mp-intake';
export const INTAKE_CHANGED_EVENT = 'pilot:intake-changed';
export const INTAKE_SET_REQUEST_EVENT = 'pilot:set-intake-field';

export interface IntakeSetRequestDetail {
  field: IntakeField;
  value: string;
}

const EMPTY_INTAKE: Intake = {industry: '', size: '', function: ''};

/** Read the current intake snapshot from localStorage. */
export function getIntake(): Intake {
  if (typeof window === 'undefined') return {...EMPTY_INTAKE};
  let stored: Partial<Intake> = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) as Partial<Intake>;
  } catch {
    /* private mode or malformed JSON */
  }
  return {
    industry: typeof stored.industry === 'string' ? stored.industry : '',
    size: typeof stored.size === 'string' ? stored.size : '',
    function: typeof stored.function === 'string' ? stored.function : '',
  };
}

/** True if any intake field is set. */
export function isIntakeSet(): boolean {
  const i = getIntake();
  return !!(i.industry || i.size || i.function);
}

/**
 * Subscribe to intake changes. Listener fires whenever PilotContext
 * dispatches `pilot:intake-changed`. Returns an unsubscribe function.
 *
 * The listener receives the latest intake snapshot for convenience —
 * subscribers don't need to call `getIntake()` themselves.
 */
export function onIntakeChange(cb: (intake: Intake) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = () => {
    try {
      cb(getIntake());
    } catch (err) {
      // Engine subscribers throwing shouldn't poison other listeners.
      console.error('[intake-bridge] subscriber threw', err);
    }
  };
  window.addEventListener(INTAKE_CHANGED_EVENT, handler);
  return () => window.removeEventListener(INTAKE_CHANGED_EVENT, handler);
}

/**
 * Request a write into the React-owned intake state. The PilotContext
 * provider listens for `pilot:set-intake-field` and calls
 * `setIntakeField` for us — that keeps React as the sole writer of
 * intake state (the bridge does not touch localStorage on write).
 *
 * Use this from non-React callers (the vanilla-JS engine) when the
 * imperative flow needs to mutate intake (e.g. the sandbox empty-state
 * "clear filter" button).
 */
export function requestSetIntakeField(field: IntakeField, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent<IntakeSetRequestDetail>(INTAKE_SET_REQUEST_EVENT, {
        detail: {field, value: value || ''},
      }),
    );
  } catch {
    /* CustomEvent unsupported */
  }
}
